import { useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import useAuth from './useAuth';
import BusinessTypeRoute from './BusinessTypeRoute';
import PricingSection from '../sections/auth/PricingSection/PricingSection';
import { useInitiateSubscription } from '../APIs/Subscription/subscription';
import { handlePaymentGateway } from '../utils/paymentGateway';
import { notify } from '@common/NotificationService';
import './ModuleRoute.css';

export default function ModuleRoute({ module: moduleName, children }) {
  const { modules, modulesSelfDisabled, user, refreshMe } = useAuth();
  const [showUpgrade, setShowUpgrade] = useState(false);
  const { initiateSubscription } = useInitiateSubscription();

  const blocked = modules !== null && modules[moduleName] === false;

  // Two different reasons a module can be off, and they need opposite answers.
  // The restaurant's own admin switching it off in Settings → Feature Settings
  // is not something to sell an upgrade for — the fix is a switch they already
  // own, and offering to charge them for it would be absurd.
  const selfDisabled = blocked && (modulesSelfDisabled || []).includes(moduleName);
  const planBlocked = blocked && !selfDisabled;
  const currentPlanId = user?.subscription?.plan_id ?? null;
  const merchantId    = user?.user?.id ?? null;

  const handleSelectPlan = async ({ planId, billing }) => {
    if (!merchantId) return;
    try {
      const payInfo = await initiateSubscription({
        merchant_id: merchantId,
        plan_id: planId,
        billing_cycle: billing || 'monthly',
        source: 'upgradation'
      });

      await handlePaymentGateway(payInfo, {
        onRazorpaySuccess: async () => {
          setShowUpgrade(false);
          await refreshMe();
        },
        onRazorpayCancel: () => notify.error('Payment cancelled.'),
        onRazorpayFail: (msg) => notify.error(msg || 'Payment failed.'),
        onSdkError: () => notify.error('Failed to load payment gateway. Please try again.')
      });
    } catch (e) {
      notify.error(e?.response?.data?.message || 'Error initiating payment. Please try again.');
    }
  };

  // BusinessTypeRoute is the outer gate on purpose. If a trade simply doesn't
  // have this feature, saying so must win over the paywall — offering to sell
  // a gym its Menus module would be worse than showing nothing at all.
  return (
    <BusinessTypeRoute module={moduleName}>
      {selfDisabled ? (
        <div className="mr-wrap">
          <div className="mr-card">
            <div className="mr-icon">
              <i className="ph ph-toggle-left" />
            </div>
            <h4 className="mr-title">Feature Switched Off</h4>
            <p className="mr-msg">
              This feature has been switched off for your restaurant. An admin can turn
              it back on under <strong>Settings → Feature Settings</strong>.
            </p>
          </div>
        </div>
      ) : planBlocked ? (
        <>
          <div className="mr-wrap">
            <div className="mr-card">
              <div className="mr-icon">
                <i className="ph ph-lock-simple" />
              </div>
              <h4 className="mr-title">Feature Locked</h4>
              <p className="mr-msg">
                This feature is available on the <strong>Pro Plan</strong>.
                Upgrade your subscription to unlock it.
              </p>
              <button className="mr-btn" onClick={() => setShowUpgrade(true)}>
                Upgrade to Pro
              </button>
            </div>
          </div>

          <Modal show={showUpgrade} onHide={() => setShowUpgrade(false)} centered size="xl">
            <Modal.Header closeButton>
              <Modal.Title>Upgrade Your Plan</Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
              <PricingSection currentPlanId={currentPlanId} onSelectPlan={handleSelectPlan} />
            </Modal.Body>
          </Modal>
        </>
      ) : (
        children
      )}
    </BusinessTypeRoute>
  );
}

ModuleRoute.propTypes = {
  module: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
};
